<template>
  <b-container fluid>
    <b-breadcrumb :items="breadcrumbs" />
    <b-row>
      <b-col md="4" lg="3">
        <b-card header="Your Report" class="mb-3">
          <p>You can select the data your report works off of using the filters section.</p>
          <p v-if="report.type === 'table'">Click on a column header to sort ascending or descending.</p>
          <p v-if="company">
            This report is assigned to
            <strong>{{company.name}}</strong>. If you make any changes here and select 'Save Changes', users will receive the updated report.
          <p v-if="company">
            You can add this report to another company by selecting 'Save a Copy' below.
          </p>
          <div v-if="admin">
            <b-btn class="mb-1" size="sm" variant="primary" @click="showSaveAsModal" v-if="report != null && reportId == null">Save Report...</b-btn>
            <b-btn class="mb-1" size="sm" variant="primary" @click="showSaveModal" v-if="report != null && reportId != null">Save Changes...</b-btn>
            <b-btn class="mb-1" size="sm" variant="primary" @click="showSaveAsModal" v-if="report != null && reportId != null">Save a Copy...</b-btn>
            <b-btn class="mb-1" size="sm" variant="danger" @click="showDeleteModal" v-if="report != null && reportId != null">Delete Report</b-btn>
          </div>
        </b-card>
      </b-col>
      <b-col md="8" lg="9">
        <b-card class="mb-3">
          <div class="pt-0 d-flex">
            <h5 class="d-flex flex-column justify-content-center mr-3">Filters:</h5>
            <filters-editor v-model="report.filters" :filterOptions="reportOptions" :report="report" :admin="admin" v-if="reportOptions" v-on:input="updateReport" class="w-100"></filters-editor>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-alert :show="errorMessage" variant="danger">{{errorMessage}}</b-alert>

    <b-row v-if="results">
      <b-col>
        <b-card class="mb-3" no-body>
          <b-card-body v-if="report.type === 'table'" class="mb-0 pb-0">
            <b-row>
              <b-col>
                <h4 class="card-title">{{report.name}}</h4>
              </b-col>
              <b-col class="text-right mb-3">
                <span class="btn btn-link disabled" v-if="showRowCount">Found {{rowCountText}}</span>
                <span class="btn btn-link disabled" v-if="showPagination">Page {{currentPage}} of {{results.page_count}}</span>
                <b-btn size="sm" @click="showColumnsModal">Select Columns...</b-btn>
                <table-options size="sm" v-if="report != null" :settings="report.settings" v-on:input="updateReport"></table-options>
                <b-dropdown size="sm" right text="Download">
                  <b-dropdown-item-button @click="downloadTable('csv')">Save CSV File...</b-dropdown-item-button>
                  <b-dropdown-item-button @click="downloadTable('xlsx')">Save Excel Spreadsheet...</b-dropdown-item-button>
                </b-dropdown>
              </b-col>
            </b-row>
          </b-card-body>

          <report-table v-if="report.type === 'table' && results != null" :results="results" :report="report"
                        v-on:reorder="updateReport" v-on:selectRow="selectRow"></report-table>
          <b-pagination-nav class="mt-3" v-if="showPagination" align="center" size="md" :number-of-pages="results.page_count" v-model="currentPage" :use-router="true" :limit="7" :link-gen="pageLink" :hideEllipsis="true" firstText="« First" lastText="Last »" prevText="‹ Previous" nextText="Next ›"></b-pagination-nav>

          <span style="position: absolute; right: 0; top: 0" class="mr-2 mt-2" v-if="showChart">
            <!-- <b-dropdown size="sm" right text="Chart Options"></b-dropdown> -->
            <b-dropdown size="sm" right text="Download">
              <b-dropdown-item-button @click="downloadChart">Save as PNG...</b-dropdown-item-button>
              <!-- // <b-dropdown-item-button @click="copyChartToClipboard">Copy Image to Clipboard</b-dropdown-item-button> -->
            </b-dropdown>
          </span>
          <div v-if="showChart" class="mr-2 ml-2">
            <donut-chart v-if="report.type === 'donut' && results != null" :report="report" :results="results" element-id="results" ref="chart" v-on:selectLink="selectLink"></donut-chart>
            <line-chart v-if="report.type === 'line' && results != null" :report="report" :results="results" element-id="results" ref="chart" v-on:selectLink="selectLink"></line-chart>
            <bar-chart v-if="report.type === 'bar' && results != null" :report="report" :results="results" element-id="results" ref="chart" v-on:selectLink="selectLink"></bar-chart>
            <stacked-bar-chart v-if="report.type === 'stacked' && results != null" :report="report" :results="results" element-id="results" ref="chart" v-on:selectLink="selectLink"></stacked-bar-chart>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-modal centered ref="columnsModal" title="Select Columns" hide-footer @hide="columnsModalHidden">
      <b-list-group>
        <draggable v-model="report.columns" handle=".handle">
          <div class="list-group-item d-flex p-2" v-for="(column, columnIndex) in report.columns" :key="`${columnsModalId}${columnIndex}`">
            <div style="width: 22px; height: 22px; background: transparent url('/static/img/icons/reorder.png') no-repeat center center; background-size: 22px 22px" class="handle align-self-center"></div>
            <div class="w-100 align-self-center">
                {{column.label}}
            </div>
            <b-button-group class="align-self-center" size="sm">
              <b-button @click="showEditModal(column)"><icon name="edit" size="16" /></b-button>
              <b-button @click="deleteColumn(columnIndex)" v-if="report.columns.length > 1"><icon name="delete" size="16" /></b-button>
            </b-button-group>
          </div>
        </draggable>
      </b-list-group>
      <div class="mt-3">
        <b-btn @click="closeColumnsModal" variant="primary" class="float-right">Close</b-btn>
        <b-btn @click="addColumn">Add a Column</b-btn>
      </div>
    </b-modal>

    <b-modal centered ok-only size="lg" ref="editColumnModal" title="Edit Column" @hide="updateColumn" v-if="reportOptions != null && report != null" ok-title="Close">
      <column-editor v-if="editColumn" :column="editColumn" :columnSpecCategories="reportOptions.column_categories" :reportType="report.type" :report="report" :isRequired="true" :isGrouping="false" :isAggregate="isAggregate" :hasLabel="true" prepend="Select Column" defaultOptionText="Please choose an option">
      </column-editor>
    </b-modal>

    <save-report-modal ref="saveModal" v-if="report != null && reportOptions != null" @reportSaved="reportSaved" :reportOptions="reportOptions"
                       :report="report" :admin="admin" :reportId="reportId" :companyId="companyId" :teamIds="teamIds">
    </save-report-modal>
    <save-report-modal ref="saveAsModal" v-if="report != null && reportOptions != null" @reportSaved="reportSaved" :reportOptions="reportOptions"
                       :report="report" :admin="admin">
    </save-report-modal>
    <b-modal centered ref="deleteReportModal" title="Delete Report" ok-title="Delete" ok-variant="danger" @ok="deleteReport">
      <p>Are you sure you want to delete this report?</p>
    </b-modal>
  </b-container>
</template>

<script>
import _ from 'lodash'
import uuid from 'uuid/v1'
import download from 'downloadjs'
import API from '@/lib/api'
import Draggable from 'vuedraggable'
import Icon from '@/components/Icon'
import FiltersEditor from '@/components/reports/FiltersEditor'
import DonutChart from '@/components/DonutChart'
import LineChart from '@/components/LineChart'
import BarChart from '@/components/BarChart'
import StackedBarChart from '@/components/StackedBarChart'
import ReportTable from '@/components/ReportTable'
import TableOptions from '@/components/reports/TableOptions'
import ColumnEditor from '@/components/reports/ColumnEditor'
import SaveReportModal from '@/components/reports/SaveReportModal'

export default {
  name: 'RunReport',
  components: { Draggable, Icon, FiltersEditor, DonutChart, BarChart, StackedBarChart, LineChart, ReportTable, TableOptions, ColumnEditor, SaveReportModal },
  data () {
    return {
      admin: this.$route.query.admin,
      report: _.assign({filters: []}, _.cloneDeep(this.$route.query.report)),
      reportOptions: null,
      results: null,
      currentPage: parseInt(this.$route.query.page || '1'),
      columnsModalId: null,
      editColumn: null,
      reportId: this.$route.query.report_id,
      companyId: this.$route.query.company_id,
      teamIds: this.$route.query.team_ids || [],
      errorMessage: null
    }
  },
  mounted () {
    this.fetchOptions()
    this.runReport()
  },
  watch: {
    '$route' (to, from) {
      this.admin = this.$route.query.admin
      this.report = _.cloneDeep(this.$route.query.report)
      this.currentPage = parseInt(this.$route.query.page || '1')
      this.runReport()
    }
  },
  computed: {
    showPagination () {
      return this.results != null && this.results.page_count > 1
    },
    showRowCount () {
      return this.results != null
    },
    rowCountText () {
      if (this.results.row_count === 1) {
        return '1 Record'
      } else {
        return `${this.results.row_count} Records`
      }
    },
    showChart () {
      return this.report.type !== 'table'
    },
    isAggregate () {
      return this.report != null && this.report.group != null && this.report.group.length > 0
    },
    breadcrumbs () {
      if (this.admin) {
        return [
          {text: 'My Vos Voco', to: '/'},
          {text: 'Admin', to: '/admin'},
          {text: 'Reports', to: '/admin/reports'},
          {text: 'Run Report', path: '/reports/run', query: {report_id: this.reportId, page: 1, admin: this.admin, report: _.cloneDeep(this.report)}}
        ]
      } else {
        return [
          {text: 'My Vos Voco', to: '/'},
          {text: 'Run Report', path: '/reports/run', query: {report_id: this.reportId, page: 1, admin: this.admin, report: _.cloneDeep(this.report)}}
        ]
      }
    },
    company () {
      if (this.companyId != null && this.reportOptions != null && this.reportOptions.companies != null) {
        const options = this.reportOptions.companies.find((c) => { return parseInt(this.companyId) === c.id })
        return options
      } else {
        return null
      }
    }
  },
  methods: {
    fetchOptions () {
      var params = {admin: this.admin, workflow_id: this.report.workflow_id}
      if (this.companyId) {
        params.company_id = this.companyId
      }
      API.get('reports/options', params, this.$store.getters.authHeaders)
        .then((response) => {
          this.reportOptions = response.data
        })
    },
    runReport () {
      var params = {admin: this.admin, report_id: this.reportId, report: this.report, page: this.currentPage}
      if (this.companyId) {
        params.company_id = this.companyId
      }
      API.post('/reports/run', params, this.$store.getters.authHeaders)
        .then((response) => {
          this.errorMessage = null
          this.results = response.data
        })
        .catch((error) => {
          if (error.response && error.response.status === 500) {
            this.errorMessage = 'Report failed to run. Please contact Vos Voco for assistance.'
          } else if (error.response.status === 401) {
            this.errorMessage = 'Sorry, you no longer have permission to run this report.'
          }
        })
    },
    updateReport () {
      this.$router.replace({path: '/reports/run', query: {report_id: this.reportId, page: 1, admin: this.admin, company_id: this.companyId, report: _.cloneDeep(this.report)}})
    },
    pageLink (page) {
      return { path: '/reports/run', query: {report_id: this.reportId, page: page, admin: this.admin, company_id: this.companyId, report: _.cloneDeep(this.report)} }
    },
    downloadChart () {
      this.$refs.chart.saveImage()
    },
    copyChartToClipboard () {
      this.$refs.chart.copyImageToClipboard()
    },
    downloadTable (format) {
      const contentType = {
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'application/csv'
      }[format]
      API.post('/reports/download', {admin: this.admin, report_id: this.reportId, report: this.report, format: format}, this.$store.getters.authHeaders, {responseType: 'blob'})
        .then((response) => {
          download(response.data, `${this.report.name}.${format}`, contentType)
        })
    },
    showColumnsModal () {
      this.columnsModalId = uuid()
      this.$refs.columnsModal.show()
    },
    closeColumnsModal () {
      this.$refs.columnsModal.hide()
    },
    columnsModalHidden () {
      this.updateReport()
    },
    deleteColumn (index) {
      this.report.columns.splice(index, 1)
    },
    addColumn () {
      var column = { column_id: null, label: null, sub_columns: [] }
      this.report.columns.push(column)
      this.showEditModal(column)
    },
    showEditModal (column) {
      this.editColumn = column
      this.$refs.editColumnModal.show()
    },
    updateColumn () {
      if (this.editColumn.column_id == null) {
        this.deleteColumn(this.report.columns.indexOf(this.editColumn))
      }
      this.editColumn = null
    },
    showSaveModal () {
      this.$refs.saveModal.show()
    },
    showSaveAsModal () {
      this.$refs.saveAsModal.show()
    },
    reportSaved () {
      if (this.admin) {
        this.$router.push('/admin/reports')
      } else {
        this.$router.push('/')
      }
    },
    showDeleteModal () {
      this.$refs.deleteReportModal.show()
    },
    deleteReport () {
      API.delete(`/reports/${this.reportId}`, this.$store.getters.authHeaders)
      .then((response) => {
        this.reportSaved()
      })
    },
    selectRow (row) {
      this.selectLink(row.link)
    },
    selectLink (link) {
      if (link) {
        if (link.type === 'event') {
          this.$router.push({path: `/events/${link.event_id}`, query: { context: 'reports' }})
        } else if (link.type === 'filter') {
          var newReport = {
            type: 'table',
            name: 'Matching Events',
            workflow_id: this.report.workflow_id,
            columns: _.cloneDeep(this.reportOptions.default_ungrouped_columns),
            settings: {
              page_size: 25,
              order: [{ column_id: 'logged_at', direction: 'descending' }]
            }
          }
          var newFilters = _.cloneDeep(link.filters)
          newFilters.forEach((filter) => {
            if (filter.type === 'column') {
              filter.lock = 'noedit'
            }
          })
          if (this.report.filters == null) {
            newReport.filters = newFilters
          } else {
            newReport.filters = _.concat(this.report.filters, newFilters)
          }
          this.$router.push({path: '/reports/run', query: {page: 1, admin: this.admin, company_id: this.companyId, report: newReport}})
        }
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>



// WEBPACK FOOTER //
// src/pages/reports/RunReport.vue